import { Add, Gallery, Trash } from 'iconsax-react'
import { useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

const MAX_IMAGE_SIZE_BYTES = 3_000_000

interface ProfilePictureFieldProps {
  name?: string
  hint?: string
  required?: boolean
}

export const ProfilePictureField = ({
  name = 'image',
  hint,
  required = false,
}: ProfilePictureFieldProps) => {
  const { t } = useTranslation()
  const { watch, setValue } = useFormContext()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [fileName, setFileName] = useState<string>('')

  const currentImage = watch(name)

  useEffect(() => {
    if (typeof currentImage === 'string' && currentImage) {
      setPreviewUrl(currentImage)
    } else if (!currentImage) {
      setPreviewUrl(null)
      setFileName('')
    }
  }, [currentImage])

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return
    const file = acceptedFiles[0]
    const reader = new FileReader()
    reader.onload = (event) => {
      const base64String = event.target?.result as string
      setValue(name, base64String, {
        shouldValidate: true,
        shouldDirty: true,
      })
      setPreviewUrl(base64String)
      setFileName(file.name)
    }
    reader.readAsDataURL(file)
  }

  const deleteImage = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setValue(name, null, { shouldValidate: true, shouldDirty: true })
    setPreviewUrl(null)
    setFileName('')
  }

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
    },
    multiple: false,
    maxSize: MAX_IMAGE_SIZE_BYTES,
    noClick: true,
  })

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-base font-medium text-neutralColor lg:text-xl">
          {t('dashboard.runReport.confirmSubjectDetails.profilePictureTitle')}
        </p>
        <Button
          type="button"
          variant="secondary"
          className="gap-2"
          onClick={open}
        >
          {previewUrl
            ? t(
                'dashboard.runReport.confirmSubjectDetails.changeProfilePicture',
              )
            : t('dashboard.runReport.confirmSubjectDetails.addProfilePicture')}
          {!previewUrl && <Add size="16" color="currentColor" />}
        </Button>
      </div>

      <div {...getRootProps()} className="flex items-center gap-5">
        <input {...getInputProps()} />
        {previewUrl ? (
          <Avatar className="h-20 w-20 shrink-0">
            <AvatarImage src={previewUrl} alt={fileName} />
            <AvatarFallback />
          </Avatar>
        ) : (
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border border-dashed border-neutralColor5">
            <Gallery
              size="36"
              color={weCheckPalette.neutralColor60}
              variant="Linear"
            />
          </div>
        )}

        {previewUrl ? (
          <>
            <p className="flex-1 truncate text-base text-neutralColor80">
              {fileName ||
                t(
                  'dashboard.runReport.confirmSubjectDetails.profilePictureLoaded',
                )}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={deleteImage}
              aria-label={t(
                'dashboard.runReport.confirmSubjectDetails.removeProfilePicture',
              )}
            >
              <Trash
                size="18"
                color={weCheckPalette.dynamicRed}
                variant="Bold"
              />
            </Button>
          </>
        ) : (
          <div className="flex max-w-[345px] flex-col gap-2">
            {required && (
              <p className="text-base font-medium text-dynamicBlue">
                {t(
                  'dashboard.runReport.confirmSubjectDetails.profilePictureRequired',
                )}
              </p>
            )}
            <p className="text-base text-neutralColor80">
              {hint ??
                t(
                  'dashboard.runReport.confirmSubjectDetails.profilePictureHint',
                )}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
